import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import CountryLabel from '../../../components/CountryLabel/CountryLabel';
import { Loader } from '../../../components/Loader/Loader';
import {
  getInProgressClientVATIds,
  getRegisteredVATIds,
} from '../../../core/api';
import { ClientTaxDetailsStatusEnum } from '../../../core/enums';
import { formatDate } from '../../../core/helpers';
import { normalizeDateFE } from '../../../core/normalization';
import { useAppSelector, useQueriesTyped } from '../../../hooks';
import './SellerVATIds.scss';
import SellerVatIdsEmpty from './SellerVatIdsEmpty';

export enum IdNumberStatusEnum {
  Requested = 2,
  Active = 1,
  Unavailable = 4,
  Deactivated = 8,
}

export const STATUS_LABELS: { [key: string]: string } = {
  waiting: 'Waiting for master data',
  new: 'Waiting for master data',
  check: 'In preparation',
  check_done: 'Preparation completed',
  docs_prep: 'Documents in progress',
  docs_ready: 'Documents prepared',
  docs_incomplete: 'Documents incomplete',
  sent_to_client: 'Documents for signature',
  received: 'Signed documents received',
  in_tax_office: 'Sent to authorities',
  to_tax_office: 'Received by Authorities',
  tax_id_received: 'Tax ID received',
  vat_id_received: 'VAT ID received',
  registered: 'Registered',
  stopped: 'Stopped',
  paused: 'Paused',
  not_needed: '',
  deregistered: 'Deregistered',
  Takeover: 'Takeover',
};

export default function SellerVATIds() {
  const { vs2912CpPrototypeV2 } = useFlags();

  const { t } = useTranslation();
  const user = useAppSelector((s) => s.user.user);
  const activeCompanyId = useAppSelector(
    (s) => s.user.activeCompany?.company_id
  );

  const [registrations, registeredVATIds] = useQueriesTyped([
    {
      queryKey: ['SellerVATIds', user!.Id, activeCompanyId],
      queryFn: () => getInProgressClientVATIds(activeCompanyId),
    },
    {
      queryKey: ['SellerRegisteredVATIds', user!.Id, activeCompanyId],
      queryFn: () => getRegisteredVATIds(activeCompanyId),
    },
  ]);

  const registered = registeredVATIds.data || [];

  const inProgress = (registrations.data || []).filter(
    (v) => v.status !== ClientTaxDetailsStatusEnum.NOT_NEEDED
  );

  if (registrations.isLoading || registeredVATIds.isLoading) return <Loader />;

  if (!registered.length && !inProgress.length) return <SellerVatIdsEmpty />;

  return (
    <div className='Page flex-grow-1'>
      <div className='container'>
        <div className='Paper Paper--sm mx-auto py-3'>
          <div className='px-4 mb-3'>
            <div className='SellerVATIds__title fw-900 mb-2'>
              {t('International VAT')}
            </div>
            <div>{t('VAT IDs under countX management')}</div>
          </div>
          {!!inProgress.length && (
            <div className='SellerVATIds__border px-4 pt-3 pb-3'>
              <div className='Text--20 fw-800 mb-2'>
                {t('VAT IDs in progress')}
              </div>
              <div className='d-grid SellerVATIds__grid mb-2'>
                <div className='Text--gray'>{t('Country')}</div>
                <div className='Text--gray'>{t('Start date')}</div>
                <div className='Text--gray'>{t('Status')}</div>
                <div className='Text--gray'>{t('Updated')}</div>
              </div>
              {inProgress.map((item, i) => (
                <div
                  className='d-grid SellerVATIds__grid mb-3'
                  key={item.Country.short_name}
                  data-testid={`inProgress-row-${i + 1}`}
                >
                  <div data-testid={`inProgress-${i + 1}-country`}>
                    <CountryLabel
                      country={item.Country.short_name}
                      link={vs2912CpPrototypeV2}
                    />
                  </div>
                  <div data-testid={`inProgress-${i + 1}-date`}>
                    {item.registration_date &&
                      formatDate(
                        normalizeDateFE(item.registration_date),
                        'dd.MM.yyyy'
                      )}
                  </div>
                  <div data-testid={`inProgress-${i + 1}-status`}>
                    {t(STATUS_LABELS[item.status])}
                  </div>
                  <div data-testid={`inProgress-${i + 1}-status_updated_date`}>
                    {item.status_updated_date ? (
                      <div>
                        {formatDate(
                          normalizeDateFE(item.status_updated_date),
                          'dd.MM.yyyy'
                        )}
                      </div>
                    ) : (
                      t('No information')
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!!registered.length && (
            <div className='SellerVATIds__border px-4 pt-3 pb-4'>
              <div className='Text--20 fw-800 mb-2'>
                {t('Registered VAT IDs')}
              </div>
              <div className='d-grid SellerVATIds__registeredGrid'>
                <div className='Text--gray'>{t('Country')}</div>
                <div className='Text--gray'>{t('VAT ID')}</div>
                <div className='Text--gray'>{t('Tax ID')}</div>
              </div>
              {registered.map((item, i) => (
                <div
                  className={classNames(
                    'd-grid SellerVATIds__registeredGrid pb-3 pt-2 mx-0',
                    {
                      'border-top': i > 0,
                    }
                  )}
                  key={i}
                  data-testid={`registered-row-${i + 1}`}
                >
                  <div>
                    <CountryLabel
                      country={item.Country.short_name}
                      link={vs2912CpPrototypeV2}
                    />
                  </div>
                  <div>
                    <Cell
                      number={item.vat_number}
                      date={item.start_date_vat_number}
                      status={item.vat_number_status}
                    />
                  </div>
                  <div>
                    <Cell
                      number={item.tax_number}
                      date={item.start_date_tax_number}
                      status={item.tax_number_status}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const Cell = ({
  number,
  status,
  date,
  delimeter,
}: {
  number: string;
  status?: IdNumberStatusEnum;
  delimeter?: string;
  date: string;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {number}
      <div className='d-flex align-items-center'>
        <div
          className={classNames({
            'Text--green': status === IdNumberStatusEnum.Active,
            'Text--gray': status !== IdNumberStatusEnum.Active,
          })}
        >
          {typeof status === 'number' && t(IdNumberStatusEnum[status])}
        </div>
        &nbsp;
        {date && status === IdNumberStatusEnum.Active && (
          <div>
            {delimeter || t('since')}&nbsp;
            {formatDate(normalizeDateFE(date), 'dd.MM.yyyy')}
          </div>
        )}
      </div>
    </>
  );
};
